import { Spinner } from '@components/spinner';
import { useAsyncData } from 'client/lib/hooks';
import { rpx } from 'client/lib/rpx-client';
import { CalendarLinkInfo, CalendarMetadata } from './calendar-links';

/**
 * Hook to load calendar link information including event type details
 */
function useCalendarLinkInfo(url: string, metadata?: CalendarMetadata) {
  return useAsyncData(async (): Promise<undefined | CalendarLinkInfo> => {
    if (!metadata?.hostId) {
      return;
    }

    const linkInfo = {
      hostId: metadata.hostId,
      eventTypeId: metadata.eventTypeId,
    } as const;

    if (!linkInfo.eventTypeId) {
      return linkInfo;
    }

    if (linkInfo.eventTypeId) {
      try {
        const eventType = await rpx.ruzcalEventTypes.getEventType({ id: linkInfo.eventTypeId });
        return { ...linkInfo, eventType };
      } catch (err) {
        // Event type might not exist or be accessible
        return { ...linkInfo, error: 'Event type not found' };
      }
    }

    return linkInfo;
  }, [url, metadata?.hostId, metadata?.eventTypeId]);
}

export function CalendarLinkDisplay({
  url,
  metadata,
}: {
  url: string;
  metadata: CalendarMetadata;
}) {
  const { data, isLoading } = useCalendarLinkInfo(url, metadata);

  if (isLoading) {
    return (
      <div class="flex items-center gap-2 p-3 border border-gray-300 rounded-md bg-gray-50">
        <Spinner class="w-4 h-4" />
        <span class="text-sm text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!data?.hostId) {
    return (
      <div class="p-3 border border-gray-300 rounded-md bg-gray-50">
        <span class="text-sm text-gray-600">Invalid calendar link</span>
      </div>
    );
  }

  return (
    <div class="flex-1 p-3 border border-gray-300 rounded-md bg-gray-50 text-left">
      <div class="flex flex-col">
        <div class="font-medium text-sm">
          {data.eventType?.name || data.error || 'Book me page'}
        </div>
        {data.eventType && (
          <div class="text-xs text-gray-500 mt-1">
            {`${data.eventType.duration} min • Direct booking link`}
          </div>
        )}
      </div>
    </div>
  );
}
